/**
 * License Criteria Manual Form Component
 *
 * Manual entry form for creating license criteria.
 * Supports four scenarios:
 * 1. Org selected + invoice available: Normal flow
 * 2. Org selected + no invoice: Creates orphaned criteria (warning shown)
 * 3. No org + orphaned invoice available: Links to invoice, auto-links to org when created
 * 4. No org + no invoice: Form disabled until prerequisites exist
 */

import type { ReactElement } from 'react'
import type { LicenseCriteria, Organisation, XeroInvoiceState } from '../../types'
import { LicenseCriteriaPrerequisitesBlock } from './LicenseCriteriaPrerequisitesBlock'
import {
  ErrorAlert,
  FormHeader,
  ScenarioAlerts,
  SuccessAlert,
} from './license-criteria-manual-form.parts'
import { useFormLookups } from './license-criteria-form-lookups'
import { useLicenseCriteriaForm } from './use-license-criteria-form'
import { useCriteriaSubmit } from './use-criteria-submit'
import { CriteriaFormFields } from './CriteriaFormFields'

interface LicenseCriteriaManualFormProps {
  organisations: Organisation[]
  xeroInvoices?: XeroInvoiceState[]
  onSuccess?: ((criteria: LicenseCriteria) => void) | undefined
  onError?: ((error: Error) => void) | undefined
}

export function LicenseCriteriaManualForm({
  organisations,
  xeroInvoices = [],
  onSuccess,
  onError,
}: LicenseCriteriaManualFormProps): ReactElement {
  const lookups = useFormLookups()
  const form = useLicenseCriteriaForm({ organisations, xeroInvoices })
  const handleSubmit = useCriteriaSubmit({ form, onSuccess, onError })

  if (form.isFormDisabled) {
    return <LicenseCriteriaPrerequisitesBlock />
  }

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm dark:border-stone-700 dark:bg-stone-800">
      <FormHeader />
      <ScenarioAlerts
        scenario={form.scenario}
        hasSelectedInvoice={form.hasSelectedInvoice}
        orphanedInvoicesCount={form.orphanedInvoices.length}
        selectedInvoice={form.selectedInvoice}
      />
      <form
        onSubmit={(e) => {
          void handleSubmit(e)
        }}
        className="space-y-4"
      >
        <CriteriaFormFields form={form} lookups={lookups} organisations={organisations} />
      </form>
      <ErrorAlert error={form.error} />
      <SuccessAlert success={form.success} scenario={form.scenario} />
    </div>
  )
}
